import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

import Badge from "components/generic/Badge";
import FeatureList from "components/generic/FeatureList";

import { Button } from "components/ui/button";
import { Progress } from "../ui/progress"
import CollapsibleTaskList from "./CollapsibleTaskList";

import { Circle, PhoneCall, Mail, Target, GalleryThumbnails, ExternalLink, FileText, X } from "lucide-react";
import { cleanBio, parseValuesToLabels } from "lib/utils";
import { Instagram } from "lucide-react";

// Import Swiper React components and styles
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Zoom, Mousewheel, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

// Add this at the very top of your file (outside the component)
if (typeof window !== "undefined") {
  const prevError = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    if (message.includes("ResizeObserver loop completed with undelivered notifications")) {
      return true; // Prevents the error from firing in the console
    }
    if (prevError) return prevError(...arguments);
  };
}

const CrewCard = ({ crewMember, onViewProfile, onApprove }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSwiperReady, setIsSwiperReady] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const closeTimerRef = useRef(null);
  const openTimerRef = useRef(null);

  // Fallback values if crewMember data is not available
  const name = `${crewMember?.first_name} ${crewMember?.last_name}` || 'Crew Member';
  //const role = crewMember?.bio ? cleanBio(crewMember?.bio) : "Role";
  const bio = crewMember?.bio || '';

  // check if crewMember has portfolio or recent works uploaded
  const memberWorks = crewMember?.crew_member_files.filter(file =>
    file.file_type === "recent_work" || file.file_type === "portfolio"
  );

  // Check if bio contains Instagram URL
  const instagramUrlRegex = /(https?:\/\/)?(www\.)?instagram\.com\/[^\s]*/i;
  const instagramMatch = bio.match(instagramUrlRegex);
  const instagramUrl = instagramMatch ? instagramMatch[0] : null;

  // Remove Instagram URL from bio text and trim whitespace
  const role = instagramUrl ? bio.replace(instagramUrlRegex, '').trim() : bio;

  // const profileImage = crewMember?.profile_image || 'images/misc/tempProfile.jpg';
  const availabilityStatus = crewMember?.is_available ? 'available' : 'busy';
  const profileType = crewMember?.is_beige_member;
  const phone = crewMember?.phone || '';
  const email = crewMember?.email || '';
  const rating = parseInt(crewMember?.rating) || 0;
  // Note: Logic was changed to parse JSON on the fly using parseList helper if needed,
  // but ps-merge-all branch seems to have cleaned up data parsing upstream or expects arrays.
  // We will keep the robust parseList logic from HEAD to be safe.

  const tasks = crewMember?.assigned_tasks

  // Determine availability badge properties
  const availabilityBadgeProps = {
    available: {
      text: 'Available',
      textColor: 'text-green-600',
      bgColor: 'bg-green-100 border border-green-300',
    },
    unavailable: {
      text: 'Unavailable',
      textColor: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
    busy: {
      text: 'On Task',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    },
  };

   const handleApproveClick = async () => {
    setIsApproving(true);
    await onApprove();
    setIsApproving(false);
  };

  const availabilityBadge = availabilityBadgeProps[availabilityStatus] || availabilityBadgeProps.available;
  const [profileImage, setProfileImage] = useState("/images/misc/profile.png");

  const parseList = (data) => {
    if (!data) return [];

    // Already an array
    if (Array.isArray(data)) return data;

    // Handle string values
    if (typeof data === "string") {
      try {
        // Try JSON.parse first
        return JSON.parse(data);
      } catch (_) {
        // Not JSON → split gracefully
        return data
          .split(/[,|]/)   // split by comma or pipe
          .map(item => item.trim()) // trim whitespace
          .filter(Boolean); // remove empty strings
      }
    }

    // Fallback
    return [];
  };

  let tasksCompleted, progressPercent = 0
  if (tasks && tasks.length > 0) {
    tasksCompleted = tasks.filter(task => (task.status == "completed" || task.status == "complete" || task.status == "done"))
    progressPercent = (tasksCompleted.length / tasks.length) * 100
  }

  useEffect(() => {
    if (crewMember?.crew_member_files?.length > 0) {
      const image = crewMember.crew_member_files.find(file => file.file_type === "profile_photo");

      if (image) {
        const url = image.file_path.includes("http") ? image.file_path : `${process.env.REACT_APP_S3_BASE_URL}/${image.file_path}`;
        setProfileImage(url);
      }
    }
  }, [crewMember]);

  const equipmentList = parseList(crewMember.equipment_ownership);
  const skillsList = parseValuesToLabels(crewMember?.skills);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      setIsModalOpen(true);
      // Wait for the CSS animation (200ms) before mounting Swiper
      setTimeout(() => setIsSwiperReady(true), 250);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsModalOpen(false);
      setIsSwiperReady(false); // Reset for next time
    }, 100);
  };

  const handleModalEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };


  return (
    <div className="border border-border bg-card rounded-2xl py-4 w-full shadow-sm">
      {/* Styles to customize Swiper colors and centering */}
      <style>{`
        .portfolio-swiper .swiper-pagination-bullet-active {
          background: #E8D1AB !important;
        }
        .portfolio-swiper .swiper-pagination-bullet {
          background: #E1CAA1 !important;
          margin-bottom: 30px;
        }
        .portfolio-swiper .swiper-button-next,
        .portfolio-swiper .swiper-button-prev {
          color: #E8D1AB !important;
          width: 20px !important;
          height: 20px !important;
          border-radius: 50%;
          after { font-size: 18px !important; font-weight: bold; }
        }
        .portfolio-swiper .swiper-zoom-container {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          height: 100%;
          width: 100%;
        }
        .portfolio-swiper .swiper-button-prev{
          margin-left: 20px !important;
        }
        .portfolio-swiper .swiper-button-next{
          margin-right: 20px !important;
        }
        .portfolio-swiper {
          min-height: 1px;
          contain: layout;
        }
        .swiper-slide {
          backface-visibility: hidden;
        }
      `}</style>

      {/* Profile info section */}
      <div className="flex flex-col md:flex-row justify-between px-4 gap-4">
        <div className="flex items-start justify-start gap-2">
          <img
            src={profileImage}
            alt={name}
            className="w-[72px] h-[72px] rounded-lg object-cover"
          />
          <div className="flex flex-col gap-1">
            <p className="text-base text-foreground font-semibold">{name}</p>
            {role && <p className="text-muted-foreground text-sm">{role}</p>}
            <div className="flex gap-2 justify-start">
              {
                (profileType == 1) ? (
                  <Badge
                    icon={<Target className="w-4 h-4 text-orange-600" />}
                    text={'Beige'}
                    textColor={"text-orange-600"}
                    bgColor={"bg-orange-50 dark:bg-orange-900/20"}
                  />) : (
                  <Badge
                    icon={<Target className="w-4 h-4 text-blue-800" />}
                    alt={"Profile Type"}
                    text={"Memehouse"}
                    textColor={"text-blue-800"}
                    bgColor={"bg-blue-50"}
                  />
                )
              }
            </div>
          </div>
        </div>

        <div className="flex md:flex-col items-center md:items-end justify-between items-end gap-2">
          <div className="flex items-center gap-2">
            <Badge
              icon={<Circle className={`w-4 h-4 ${availabilityBadge.textColor}`} />}
              text={availabilityBadge.text}
              textColor={availabilityBadge.textColor}
              bgColor={availabilityBadge.bgColor}
            />
            {instagramUrl && (
              <a
                href={instagramUrl.startsWith('http') ? instagramUrl : `https://${instagramUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-md bg-pink-50 hover:bg-pink-100 dark:bg-pink-900/20 dark:hover:bg-pink-900/30 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <Instagram className="w-4 h-4 text-pink-600 dark:text-pink-400" />
              </a>
            )}
          </div>
          {rating > 0 && (
            <p className="inline-flex gap-1 text-muted-foreground text-sm">
              ⭐
              {rating.toFixed(1)}
            </p>
          )}
        </div>

      </div>

      {(phone || email || memberWorks) && (
        <div className="flex flex-wrap gap-2 m-4 relative">
          {phone &&
            <Badge
              icon={<PhoneCall className="w-4 h-4 text-dark-blue dark:text-indigo-400" />}
              text={phone}
              textColor={"text-dark-blue dark:text-indigo-400"}
              bgColor={"bg-indigo-50 dark:bg-indigo-900/20"}
            />
          }
          {email &&
            <Badge
              icon={<Mail className="w-4 h-4 text-dark-blue dark:text-indigo-400" />}
              text={email}
              textColor={"text-dark-blue dark:text-indigo-400"}
              bgColor={"bg-indigo-50 dark:bg-indigo-900/20"}
            />
          }

          {memberWorks.length > 0 && (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="cursor-pointer">
              <Badge
                icon={<GalleryThumbnails className="w-4 h-4 text-dark-blue dark:text-indigo-400" />}
                text={"Portfolio"}
                textColor={"text-dark-blue dark:text-indigo-400"}
                bgColor={"bg-indigo-50 dark:bg-indigo-900/20"}
              />
            </div>
          )}
        </div>
      )}

      {/* --- PARTIAL SCREEN MODAL --- */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onMouseEnter={handleModalEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="w-full max-w-3xl h-[70vh] bg-popover rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex justify-between items-center p-5 pb-6 border-b border-border bg-white dark:bg-gray-800 z-10">
              <h2 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                <GalleryThumbnails size={18} className="text-accent" /> {crewMember?.first_name}'s Portfolio
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Swiper Slider */}
            <div className="flex-1 relative bg-gray-50 dark:bg-black/40 overflow-hidden flex items-center justify-center">
              {isSwiperReady ? (
                <Swiper
                  modules={[Navigation, Pagination, Zoom, Mousewheel, Keyboard]}
                  navigation
                  pagination={{ clickable: true, dynamicBullets: true }}
                  zoom={true}
                  mousewheel={{ forceToAxis: true }}
                  keyboard={{ enabled: true }}
                  className="w-full h-full portfolio-swiper"
                >
                  {memberWorks.map((work, idx) => {
                    // Variables must be declared inside a block {} when using .map in JSX
                    const s3Url = work.file_path.includes("http")
                      ? work.file_path
                      : `${process.env.REACT_APP_S3_BASE_URL}/${work.file_path}`;

                    const isImage = work.file_path.match(/\.(jpeg|jpg|gif|png|webp)$/i);
                    const isPDF = work.file_path.match(/\.(pdf)$/i);
                    const isVideo = work.file_path.match(/\.(mp4|webm|ogg|mov)$/i);

                    return (
                      <SwiperSlide key={idx}>
                        <div className="swiper-zoom-container p-5 flex items-center justify-center h-full w-full">
                          {isImage ? (
                            <img
                              src={s3Url}
                              alt="Work"
                              className="max-w-[90%] max-h-[90%] object-contain rounded-lg shadow-md"
                            />
                          ) : isVideo ? (
                            <div className="w-[90%] h-[90%] flex items-center justify-center">
                              <video
                                src={s3Url}
                                controls
                                className="max-w-full max-h-full rounded-xl shadow-lg bg-black"
                                playsInline
                                onPointerDown={(e) => e.stopPropagation()}
                              >
                                Your browser does not support the video tag.
                              </video>
                            </div>
                          ) : isPDF ? (
                            <div className="w-[90%] h-[90%] flex flex-col items-center">
                              <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(s3Url)}&embedded=true`}
                                className="w-full h-full rounded-xl bg-white border shadow-sm"
                                frameBorder="0"
                              />
                              <a
                                href={s3Url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 text-xs font-bold text-indigo-600 flex items-center gap-1 hover:underline"
                              >
                                <ExternalLink size={12} /> View Original PDF
                              </a>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-4">
                              <FileText size={80} className="text-gray-300" />
                              <p className="text-sm font-medium">{work.file_name || "Attachment"}</p>
                              <a
                                href={s3Url}
                                download
                                className="bg-indigo-600 text-white px-8 py-2 rounded-full text-xs font-bold hover:bg-indigo-700 transition-colors"
                              >
                                Download File
                              </a>
                            </div>
                          )}
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin" />
                  <p className="animate-pulse text-sm font-medium text-muted-foreground">Initializing Gallery...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(tasks && tasks.length > 0) && (
        <>
          <div className="flex flex-col gap-2 m-4">
            <div className="flex justify-between">
              <p className="text-sm font-semibold text-foreground">Task Progress</p>
              <p className="text-sm font-semibold">{`${tasksCompleted.length}/${tasks.length} completed`}</p>
            </div>
            <Progress
              value={progressPercent}
              className={"w-full h-2 rounded-full bg-indigo-600/20 [&>div]:bg-dark-blue [&>div]:rounded-full"}
            />
          </div>
          <hr className="border-border" />
          <CollapsibleTaskList tasks={tasks} />
        </>
      )}
      <hr className="border-border" />
      {/* Equipments and Skills */}
      <div className="flex flex-col w-full">
        {
          equipmentList.length > 0 &&
          <FeatureList
            title={"Equipment"}
            list={equipmentList}
            visibleCount={4}
          />
        }
        {
          skillsList.length > 0 &&
          <FeatureList
            title={"Skills"}
            list={skillsList}
            visibleCount={4}
          />
        }
      </div>
      <hr className="border-t-2 border-border/30" />
      {/* Buttons section */}
       <div className="flex items-center justify-between gap-2 px-4 pt-4">

        {/* IF DRAFT: Show Approve Button. IF NOT DRAFT: Show Assign Task */}
        {crewMember?.is_draft === true ? (
          <Button
            onClick={handleApproveClick}
            disabled={isApproving}
            className="bg-green-600 hover:bg-green-700 text-white rounded-lg w-full flex justify-center items-center font-sm py-2 h-9"
          >
            {isApproving ? "Approving..." : "Approve Crew Member"}
          </Button>
        ) : (
          <Button className="bg-button-generic text-button-generic-foreground hover:bg-button-generic/60 rounded-lg w-full flex justify-center items-center font-sm py-2 h-9">
            <Link
              to="/crew-management/assign-task"
              state={{ crewMember }}
              className="flex justify-center items-center"
            >
              Assign Task
            </Link>
          </Button>
        )}

        <Button
          className="w-full bg-button-coloured hover:bg-button-coloured/80 rounded-lg text-button-coloured-foreground text-sm font-medium flex justify-center items-center py-2 h-9"
          onClick={onViewProfile}
        >
          View Profile
        </Button>
      </div>
    </div>
  );
};

export default CrewCard;
